import {ReactElement, useContext, useMemo} from 'react';
import clsx from 'clsx';
import {ListboxItemProps} from '@common/ui/forms/listbox/item';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {useLogout} from '@common/auth/requests/logout';
import {useAuth} from '@common/auth/use-auth';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {DialogTrigger} from '@common/ui/overlays/dialog/dialog-trigger';
import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {useDialogContext} from '@common/ui/overlays/dialog/dialog-context';
import {Trans} from '@common/i18n/trans';
import {useSlidingPill} from '@common/ui/navigation/use-sliding-pill';

const HIGHLIGHT_BORDER = '#3C7D44';
const PANEL_BORDER = '#E9ECF0';
const TEXT_COLOR = '#4C4C4C';
const LOGOUT_COLOR = '#D64242';

type MenuKey = 'account' | 'settings' | 'admin' | 'logout';

interface Props {
  children: ReactElement;
  /** @deprecated Extra items are unused in the Figma account menu. */
  items?: ReactElement<ListboxItemProps>[];
}

export function NavbarAuthMenuNew({children}: Props) {
  const {user} = useAuth();
  if (!user) return null;

  return (
    <DialogTrigger type="popover" placement="bottom" offset={20}>
      {children}
      <AuthDropdownPanel />
    </DialogTrigger>
  );
}

function AuthDropdownPanel() {
  const {auth} = useContext(SiteConfigContext);
  const {user, hasPermission} = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const {close} = useDialogContext();
  const isAdmin = hasPermission('admin');

  const keys = useMemo(() => {
    const next: MenuKey[] = ['account', 'settings'];
    if (isAdmin) next.push('admin');
    next.push('logout');
    return next;
  }, [isAdmin]);

  const {
    trackRef,
    setItemRef,
    setHoveredKey,
    pillStyle,
    pillTransitionClass,
  } = useSlidingPill(keys, null);

  if (!user) return null;

  const go = (path: string) => {
    close();
    navigate(path);
  };

  const accountPath = auth.getUserProfileLink
    ? auth.getUserProfileLink(user)
    : '/account-settings';

  const items: {
    key: MenuKey;
    label: string;
    onSelect: () => void;
    danger?: boolean;
  }[] = [
    {
      key: 'account',
      label: 'Account',
      onSelect: () => go(accountPath),
    },
    {
      key: 'settings',
      label: 'Settings',
      onSelect: () => go('/settings'),
    },
    ...(isAdmin
      ? [
          {
            key: 'admin' as const,
            label: 'Admin Settings',
            onSelect: () => go('/admin'),
          },
        ]
      : []),
    {
      key: 'logout',
      label: 'Logout',
      danger: true,
      onSelect: () => {
        close();
        logout.mutate();
      },
    },
  ];

  return (
    <Dialog
      size="w-[179px]"
      maxWidth="max-w-none"
      radius="rounded-[24px]"
      background="bg-[#F8F9FF]"
      className={clsx(
        'overflow-hidden border-2 p-0',
        '!border-[#E9ECF0] !shadow-[1px_1px_50px_1px_rgba(0,0,0,0.05)]',
      )}
      style={{borderColor: PANEL_BORDER}}
      role="menu"
      aria-label="Account menu"
    >
      <div
        ref={trackRef}
        className="relative flex w-full flex-col"
        onMouseLeave={() => setHoveredKey(null)}
      >
        <div
          aria-hidden
          className={clsx(
            'pointer-events-none absolute z-0 rounded-[24px]',
            'border-[2px] border-solid bg-white',
            pillTransitionClass,
            pillStyle ? 'opacity-100' : 'opacity-0',
          )}
          style={{
            borderColor: HIGHLIGHT_BORDER,
            left: pillStyle?.left ?? 0,
            top: pillStyle?.top ?? 0,
            width: pillStyle?.width ?? 0,
            height: pillStyle?.height ?? 0,
          }}
        />
        {items.map(item => (
          <div
            key={item.key}
            ref={setItemRef(item.key)}
            onMouseEnter={() => setHoveredKey(item.key)}
            className="relative z-[1] h-[60px] w-full shrink-0"
          >
            <button
              type="button"
              role="menuitem"
              className={clsx(
                'flex h-full w-full items-center justify-center',
                'border-0 bg-transparent p-0 text-[17px] font-normal leading-normal',
                'appearance-none outline-none',
              )}
              style={{color: item.danger ? LOGOUT_COLOR : TEXT_COLOR}}
              onClick={item.onSelect}
            >
              <Trans message={item.label} />
            </button>
          </div>
        ))}
      </div>
    </Dialog>
  );
}
